<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                Flag Ownership & Coverage
                            </h2>
                        </div>
                    </div>
                    <!-- Denied state -->
                    <zen-denied-state
                        v-if="!canView"
                        title="Flag ownership & coverage restricted"
                        message="Flag ownership & coverage needs a higher plan — no sales call, just upgrade in Billing."
                    />

                    <template v-if="canView">
                        <project-change-notice
                            :project-key="current.projectKey"
                            @refresh="load"
                        />
                        <div
                            class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                        >
                            <div>
                                <label
                                    for="flag-ownership-filter-status"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Ownership
                                </label>
                                <select
                                    id="flag-ownership-filter-status"
                                    v-model="filters.ownershipStatus"
                                    data-testid="flag-ownership-filter-status"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All flags</option>
                                    <option value="owned">Owned</option>
                                    <option value="unowned">Unowned</option>
                                </select>
                            </div>
                            <div>
                                <label
                                    for="flag-ownership-filter-team"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Team
                                </label>
                                <select
                                    id="flag-ownership-filter-team"
                                    v-model="filters.teamUlid"
                                    data-testid="flag-ownership-filter-team"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All teams</option>
                                    <option
                                        v-for="team in teams"
                                        :key="team.ulid"
                                        :value="team.ulid"
                                    >
                                        {{ team.name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    data-testid="flag-ownership-apply-filters"
                                    @click="applyFilters"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <zen-message-block v-if="error" type="error">
                            {{
                                error.message ||
                                'Failed to load flag ownership & coverage report. Please try again.'
                            }}
                        </zen-message-block>

                        <template v-if="!loading && !error">
                            <dl
                                class="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-taupe-50 ring-1 ring-taupe-200 sm:grid-cols-4"
                                data-testid="flag-ownership-summary"
                            >
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Total Flags
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="flag-ownership-summary-total"
                                    >
                                        {{ summary.total_flags ?? 0 }}
                                    </dd>
                                </div>
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Ownership Coverage
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="flag-ownership-summary-owned-percentage"
                                    >
                                        {{ summary.owned_percentage ?? 0 }}%
                                    </dd>
                                    <dd class="text-xs text-taupe-500">
                                        {{ summary.owned_count ?? 0 }} owned
                                    </dd>
                                </div>
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Description Coverage
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="flag-ownership-summary-described-percentage"
                                    >
                                        {{ summary.described_percentage ?? 0 }}%
                                    </dd>
                                    <dd class="text-xs text-taupe-500">
                                        {{ summary.described_count ?? 0 }}
                                        documented
                                    </dd>
                                </div>
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Missing Both
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="flag-ownership-summary-missing-both"
                                    >
                                        {{ summary.missing_both_count ?? 0 }}
                                    </dd>
                                </div>
                            </dl>

                            <div
                                class="-mx-4 mt-8 overflow-hidden shadow-sm sm:-mx-6 md:mx-0 md:rounded-lg"
                            >
                                <table
                                    class="min-w-full divide-y divide-taupe-300"
                                >
                                    <thead class="bg-taupe-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                class="w-full lg:w-2/5 py-3.5 pl-4 pr-3 text-left text-sm lg:text-base font-medium text-taupe-800 sm:pl-6"
                                            >
                                                <button
                                                    type="button"
                                                    class="flex items-center space-x-1 hover:text-sky-500"
                                                    @click="toggleSort('name')"
                                                >
                                                    <span>Flag</span>
                                                    <span
                                                        v-if="
                                                            filters.sortBy ===
                                                            'name'
                                                        "
                                                        >{{
                                                            sortIndicator
                                                        }}</span
                                                    >
                                                </button>
                                            </th>
                                            <th
                                                scope="col"
                                                class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                <button
                                                    type="button"
                                                    class="flex items-center space-x-1 hover:text-sky-500"
                                                    @click="toggleSort('owner')"
                                                >
                                                    <span>Owner</span>
                                                    <span
                                                        v-if="
                                                            filters.sortBy ===
                                                            'owner'
                                                        "
                                                        >{{
                                                            sortIndicator
                                                        }}</span
                                                    >
                                                </button>
                                            </th>
                                            <th
                                                scope="col"
                                                class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                Description
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        class="divide-y divide-taupe-200 bg-white"
                                    >
                                        <tr
                                            v-for="flag in flags"
                                            :key="flag.flag_key"
                                            :class="
                                                flag.missing_owner_and_description
                                                    ? 'bg-red-50'
                                                    : ''
                                            "
                                            :data-testid="
                                                'flag-ownership-row-' +
                                                flag.flag_key
                                            "
                                        >
                                            <td
                                                class="w-full max-w-0 py-4 pl-4 pr-3 text-sm lg:text-base font-medium text-taupe-800 sm:w-auto sm:max-w-none sm:pl-6"
                                            >
                                                <div>
                                                    {{ flag.flag_name }}
                                                    <span
                                                        v-if="
                                                            flag.missing_owner_and_description
                                                        "
                                                        class="ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800"
                                                        data-testid="flag-ownership-missing-both-badge"
                                                    >
                                                        Missing owner &amp;
                                                        description
                                                    </span>
                                                </div>
                                                <div
                                                    class="font-light font-mono text-sm text-taupe-800"
                                                >
                                                    <router-link
                                                        v-if="flag.flag_key"
                                                        :to="{
                                                            name: 'ProjectFlagDetailScreen',
                                                            params: {
                                                                projectKey:
                                                                    current.projectKey,
                                                                environmentKey:
                                                                    current.environmentKey,
                                                                key: flag.flag_key,
                                                            },
                                                        }"
                                                        class="flex space-x-1 text-taupe-500 hover:text-sky-500"
                                                    >
                                                        <span>{{
                                                            flag.flag_key
                                                        }}</span>
                                                        <zen-icon-open-link
                                                            css-classes="h-4 w-4"
                                                            stroke-width="2"
                                                        />
                                                    </router-link>
                                                    <span v-else>—</span>
                                                </div>
                                                <dl
                                                    class="font-light lg:hidden"
                                                >
                                                    <dt class="sr-only">
                                                        Owner
                                                    </dt>
                                                    <dd
                                                        class="mt-1 truncate text-taupe-800"
                                                    >
                                                        {{
                                                            flag.owner?.name ??
                                                            'Unassigned'
                                                        }}
                                                    </dd>
                                                    <dt class="sr-only">
                                                        Description
                                                    </dt>
                                                    <dd
                                                        class="mt-1 truncate text-taupe-800"
                                                    >
                                                        {{
                                                            flag.description ??
                                                            'No description'
                                                        }}
                                                    </dd>
                                                </dl>
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                {{
                                                    flag.owner?.name ??
                                                    'Unassigned'
                                                }}
                                                <div
                                                    v-if="flag.owner?.email"
                                                    class="text-xs text-taupe-500"
                                                >
                                                    {{ flag.owner.email }}
                                                </div>
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                <span
                                                    v-if="flag.has_description"
                                                    >{{
                                                        flag.description
                                                    }}</span
                                                >
                                                <span
                                                    v-else
                                                    class="text-taupe-500 italic"
                                                    >No description</span
                                                >
                                            </td>
                                        </tr>
                                        <tr v-if="flags.length === 0">
                                            <td colspan="3">
                                                <zen-empty-table-message
                                                    compact
                                                    data-testid="flag-ownership-empty"
                                                    icon="ZenIconFlag"
                                                >
                                                    No flags match the current
                                                    filters.
                                                </zen-empty-table-message>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>

                        <zen-pagination
                            v-if="!loading && !error"
                            :page="pagination.page"
                            :total-pages="pagination.total_pages"
                            :total="pagination.total"
                            @change="changePage"
                        />

                        <div v-if="!loading" class="text-sm pb-10">
                            Report Generated:
                            <zen-date-time :value="generatedAt" />
                        </div>
                    </template>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import ReportService from '@/services/report.service'
import TeamService from '@/services/team.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { usePlan } from '@/composables/usePlan'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { useServerPagination } from '@/composables/useServerPagination'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

export default {
    components: { ProjectChangeNotice },
    name: 'FlagOwnershipCoverage',
    setup() {
        const { current } = useCurrentEnvironment()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()
        const { pagination, setPage, resetPage, applyResponse, resetOnError } =
            useServerPagination({ perPage: 20 })
        return {
            current,
            hasPlanFeature,
            loading,
            error,
            run,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            resetOnError,
        }
    },
    data() {
        return {
            summary: {},
            flags: [],
            teams: [],
            generatedAt: null,
            filters: {
                ownershipStatus: '',
                teamUlid: '',
                sortBy: 'name',
                sortDir: 'asc',
            },
        }
    },
    computed: {
        canView() {
            return this.hasPlanFeature('flag_lifecycle_management')
        },
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        sortIndicator() {
            return this.filters.sortDir === 'asc' ? '▲' : '▼'
        },
    },
    watch: {
        environmentScopeKey() {
            this.applyFilters()
        },
    },
    methods: {
        applyFilters() {
            this.resetPage()
            this.load()
        },
        changePage(page) {
            this.setPage(page)
            this.load()
        },
        toggleSort(sortBy) {
            if (this.filters.sortBy === sortBy) {
                this.filters.sortDir =
                    this.filters.sortDir === 'asc' ? 'desc' : 'asc'
            } else {
                this.filters.sortBy = sortBy
                this.filters.sortDir = 'asc'
            }

            this.applyFilters()
        },
        loadTeams() {
            TeamService.list()
                .then((response) => {
                    this.teams = response.data ?? []
                })
                .catch(() => {
                    this.teams = []
                })
        },
        load() {
            if (!this.canView) return

            this.run(
                (signal) =>
                    ReportService.getFlagOwnershipCoverage(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            teamUlid: this.filters.teamUlid || undefined,
                            ownershipStatus:
                                this.filters.ownershipStatus || undefined,
                            sortBy: this.filters.sortBy,
                            sortDir: this.filters.sortDir,
                            page: this.pagination.page,
                            perPage: this.pagination.per_page,
                        },
                        { signal }
                    ),
                (response) => {
                    this.summary = response.data.summary
                    this.flags = response.data.flags
                    this.generatedAt = response.data.generated_at
                    if (this.applyResponse(response.data.pagination)) {
                        this.load()
                    }
                },
                () => {
                    this.summary = {}
                    this.flags = []
                    this.resetOnError()
                }
            )
        },
    },
    mounted() {
        if (this.canView) {
            this.loadTeams()
        }
        this.load()
    },
}
</script>
